import { FetchResult } from 'apollo-boost';

import {
    addContentPromotionAdGroupsMutation,
    addInternalGroups,
    copyAdGroupsMutation,
    createMcBannerAdGroup,
    createSmartAdGroup,
    createTextAdGroup,
    remoderateAdsCalloutsMutation,
    saveAdGroup,
    updateContentPromotionAdGroupsMutation,
    updateCpmAdGroup,
    updateInternalGroup
} from '../../graphql/mutations/grid/groups';
import { updateAdGroupMinusKeywordsMutation } from '../../graphql/mutations/grid/minus-phrase';
import {
    mapInternalGroupsAddResponseFromApiFormat,
    mapInternalGroupsUpdateResponseFromApiFormat
} from '../../mappers/internalGroup';
import { AddContentPromotionAdGroupsMutation } from '../../typings/graphql-grid';
import {
    addInternalAdGroups,
    CopyAdGroupsMutation,
    CreateMcBannerAdGroup,
    CreateSmartAdGroup,
    CreateTextAdGroup,
    GdAddContentPromotionAdGroupInput,
    GdAddContentPromotionAdGroupItemInput,
    GdAddInternalAdGroupsInput,
    GdAddMcBannerAdGroupItemInput,
    GdAddSmartAdGroupItemInput,
    GdAddTextAdGroupItemInput,
    GdCopyAdGroupsInput,
    GdRemoderateAdsCalloutsInput,
    GdUpdateAdGroupMinusKeywordsInput,
    GdUpdateContentPromotionAdGroupInput,
    GdUpdateContentPromotionAdGroupItemInput,
    GdUpdateCpmAdGroupInput,
    GdUpdateInternalAdGroupsInput,
    GdUpdateMcBannerAdGroupItemInput,
    GdUpdatePerformanceAdGroupItemInput,
    GdUpdateTextAdGroupItemInput,
    RemoderateAdsCallouts,
    SaveAdGroup,
    updateAdGroupMinusKeywords,
    UpdateContentPromotionAdGroups,
    UpdateCpmAdGroup,
    updateInternalAdGroups
} from '../../typings/graphql-grid';
import { InternalGroupsSaveResponse } from '../../typings/internalGroups';
import { Omit } from '../../typings/utils';
import { BaseMutationExecutor } from '../BaseMutationExecutor/BaseMutationExecutor';

export interface SaveGroupsInput {
    textAddInput: Array<GdAddTextAdGroupItemInput>;
    textUpdateInput: Array<GdUpdateTextAdGroupItemInput>;
    smartUpdateInput: Array<GdUpdatePerformanceAdGroupItemInput>;
    mcBannerUpdateInput: Array<GdUpdateMcBannerAdGroupItemInput>;
    contentPromotionAddInput: Array<GdAddContentPromotionAdGroupItemInput>;
    contentPromotionUpdateInput: Array<GdUpdateContentPromotionAdGroupItemInput>;
}

export class GroupsMutationExecutor extends BaseMutationExecutor {
    public async saveGroups(
        input: SaveGroupsInput,
        login: string
    ): Promise<FetchResult<SaveAdGroup & { notSynced?: boolean }>> {
        const {
            textAddInput,
            textUpdateInput,
            smartUpdateInput,
            mcBannerUpdateInput,
            contentPromotionAddInput,
            contentPromotionUpdateInput
        } = input;

        return this.client.awaitMutate<SaveAdGroup>({
            mutation: saveAdGroup,
            variables: {
                textAddInput,
                textUpdateInput,
                smartUpdateInput,
                mcBannerUpdateInput,
                contentPromotionAddInput,
                contentPromotionUpdateInput,
                login
            },
            login
        });
    }

    public updateGroupsMinusPhraseBind(
        login: string,
        input: GdUpdateAdGroupMinusKeywordsInput
    ): Promise<FetchResult<updateAdGroupMinusKeywords>> {
        return this.client.mutate<updateAdGroupMinusKeywords>({
            mutation: updateAdGroupMinusKeywordsMutation,
            variables: {
                input,
                login
            }
        });
    }

    // todo удалить после открытия фичи на 100% DIRECT-109443
    public async createTextGroup(
        textInput: GdAddTextAdGroupItemInput,
        login: string
    ): Promise<FetchResult<CreateTextAdGroup>> {
        return this.client.awaitMutate<CreateTextAdGroup>({
            mutation: createTextAdGroup,
            variables: {
                textInput,
                login
            },
            login
        });
    }

    public async createMcBannerGroup(
        mcBannerInput: GdAddMcBannerAdGroupItemInput,
        login: string
    ): Promise<FetchResult<CreateMcBannerAdGroup>> {
        return this.client.awaitMutate<CreateMcBannerAdGroup>({
            mutation: createMcBannerAdGroup,
            variables: {
                mcBannerInput,
                login
            },
            login
        });
    }

    public async updateInternalGroups(
        internalInput: GdUpdateInternalAdGroupsInput,
        login: string
    ): Promise<InternalGroupsSaveResponse> {
        return this.client
            .awaitMutate<updateInternalAdGroups>({
                mutation: updateInternalGroup,
                variables: {
                    internalInput
                },
                login
            })
            .then(mapInternalGroupsUpdateResponseFromApiFormat);
    }

    public async createSmartGroup(
        smartInput: GdAddSmartAdGroupItemInput,
        login: string
    ): Promise<FetchResult<CreateSmartAdGroup>> {
        return this.client
            .awaitMutate<CreateSmartAdGroup>({
                mutation: createSmartAdGroup,
                variables: {
                    smartInput,
                    login
                },
                login
            })
            .then(data => {
                // TODO убрать в DIRECT-98677
                if (data.data === undefined) {
                    return data;
                } else {
                    const { addedAdGroupItems } = data.data.addSmartAdGroups;

                    return {
                        ...data,
                        data: {
                            ...data.data,
                            addSmartAdGroups: {
                                ...data.data.addSmartAdGroups,
                                addedAdGroupItems: addedAdGroupItems.map(item => {
                                    return item === null
                                        ? item
                                        : {
                                              ...item,
                                              adGroupId: String(item.adGroupId)
                                          };
                                })
                            }
                        }
                    };
                }
            });
    }

    public async sendRemoderateAdsCallouts(
        input: Omit<GdRemoderateAdsCalloutsInput, 'moderateAccept'>,
        login: string
    ): Promise<RemoderateAdsCallouts> {
        return await this.client
            .awaitMutate<RemoderateAdsCallouts>({
                mutation: remoderateAdsCalloutsMutation,
                variables: {
                    input: {
                        ...input,
                        moderateAccept: false
                    },
                    login
                },
                login
            })
            .then((result: FetchResult<RemoderateAdsCallouts>) => result.data as RemoderateAdsCallouts);
    }

    public async sendRemoderateAcceptAdsCallouts(
        input: Omit<GdRemoderateAdsCalloutsInput, 'moderateAccept'>,
        login: string
    ): Promise<RemoderateAdsCallouts> {
        return await this.client
            .awaitMutate<RemoderateAdsCallouts>({
                mutation: remoderateAdsCalloutsMutation,
                variables: {
                    input: {
                        ...input,
                        moderateAccept: true
                    },
                    login
                },
                login
            })
            .then((result: FetchResult<RemoderateAdsCallouts>) => result.data as RemoderateAdsCallouts);
    }

    public async duplicateGroups(input: GdCopyAdGroupsInput, login: string): Promise<CopyAdGroupsMutation> {
        return await this.client
            .awaitMutate<CopyAdGroupsMutation>({
                mutation: copyAdGroupsMutation,
                variables: {
                    input,
                    login
                },
                login
            })
            .then((result: FetchResult<CopyAdGroupsMutation>) => result.data as CopyAdGroupsMutation);
    }

    public async addInternalGroups(
        internalInput: GdAddInternalAdGroupsInput,
        login: string
    ): Promise<InternalGroupsSaveResponse> {
        return this.client
            .awaitMutate<addInternalAdGroups>({
                mutation: addInternalGroups,
                variables: {
                    internalInput,
                    login
                },
                login
            })
            .then(mapInternalGroupsAddResponseFromApiFormat);
    }

    public async updateCpmGroups(
        cpmInput: GdUpdateCpmAdGroupInput,
        login: string
    ): Promise<FetchResult<UpdateCpmAdGroup & { notSynced?: boolean }>> {
        return this.client.awaitMutate<UpdateCpmAdGroup>({
            mutation: updateCpmAdGroup,
            variables: {
                cpmInput,
                login
            },
            login
        });
    }

    public async addContentPromotionGroups(
        contentPromotionInput: GdAddContentPromotionAdGroupInput,
        login: string
    ): Promise<FetchResult<AddContentPromotionAdGroupsMutation>> {
        return this.client.awaitMutate<AddContentPromotionAdGroupsMutation>({
            mutation: addContentPromotionAdGroupsMutation,
            variables: {
                contentPromotionInput
            },
            login
        });
    }

    public async updateContentPromotionGroups(
        contentPromotionInput: GdUpdateContentPromotionAdGroupInput,
        login: string
    ): Promise<FetchResult<UpdateContentPromotionAdGroups>> {
        return this.client.awaitMutate<UpdateContentPromotionAdGroups>({
            mutation: updateContentPromotionAdGroupsMutation,
            variables: {
                contentPromotionInput
            },
            login
        });
    }
}
